import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Chip,
} from '@mui/material';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  email: string;
  profile_image_url: string;
  is_admin: boolean;
}

export default function EditUser () {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const userRes = await axios.get(`/api/users?id=${id}`);
      setUser(userRes.data.user);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Error loading user data');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (!user) return;
    
    setUser(prev => ({
      ...prev!,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      
      // Validate required fields
      if (!user.username) throw new Error('Username is required');
      if (!user.email) throw new Error('Email is required');
      
      // Append basic user fields
      Object.entries(user).forEach(([key, value]) => {
        if (value && key !== 'profile_image_url') {
          formData.append(key, value.toString().trim());
        }
      });
      
      // Append profile image if selected
      if (profileImage) {
        formData.append('profile_image', profileImage);
      }

      await axios.put(`/api/users?id=${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert('User updated successfully!');
      navigate('/admin');
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err instanceof Error ? err.message : 'Error updating user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container>
        <Typography>User not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Edit User</Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={user.username}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={user.email}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            {/* Profile Image Upload */}
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Update Profile Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {profileImage && (
                <Chip
                  label={profileImage.name}
                  onDelete={() => setProfileImage(null)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* Submit Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/admin')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};
